import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { listIntegrations } from '../api/integrations';
import { analyseTestQuestionDifference } from '../api/cddi';
import { getUserId } from '../firebase';
import { getChallengeActivity, getChallengeStatus, uploadChallenge, deleteChunks } from '../api/challenges';
import {
  fetchWorkgroupDetails,
  fetchIntegrationsCollection,
  fetchIntegrationTests,
  fetchIntegrationCheckpoints,
  fetchIntegrationImprovements,
  fetchIntegrationBatchDetails,
  fetchIntegrationDisplayChallenge,
  fetchReducedDisplayChallengeById,
  fetchBatchAnswers,
  canAccessDevs,
} from './firestoreFunctions';
import { WorkgroupSelect } from './WorkgroupSelect';
import ChallengeDetailsPanel from './ChallengeDetailsPanel';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from './ui/card';
import { Select } from './ui/select';
import ImageUploadDropzone from './ImageUploadDropzone';

const percent = (num) => (num * 100).toFixed(0) + '%';

const CDDIResults = () => {
  const DEV_CHECKING_MESSAGE = 'Checking developer permissions…';
  const DEV_RESTRICTED_MESSAGE = 'Test details are restricted to developer accounts.';
  const [integrations, setIntegrations] = useState([]);
  const [selectedIntegration, setSelectedIntegration] = useState('');
  const [tests, setTests] = useState([]);
  const [selectedTest, setSelectedTest] = useState('');
  const [checkpoints, setCheckpoints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Improvements (training process analysis) state
  const [improvements, setImprovements] = useState([]);
  const [improvementsLoading, setImprovementsLoading] = useState(false);
  const [improvementsError, setImprovementsError] = useState('');
  const [selectedImprovementIdx, setSelectedImprovementIdx] = useState(0);
  const [showImprovementModal, setShowImprovementModal] = useState(false);
  const [showImprovementInternals, setShowImprovementInternals] = useState(false);
  // Per-improvement modal section visibility (must be top-level to keep hook order stable)
  const [showImpAnalysis, setShowImpAnalysis] = useState(true);
  const [showImpGenericInstructions, setShowImpGenericInstructions] = useState(true);
  const [showImpGenericLearningPrompt, setShowImpGenericLearningPrompt] = useState(true);
  const [showImpIntegrationLearningPrompt, setShowImpIntegrationLearningPrompt] = useState(true);
  const [showImpAnswerCheckerPrompt, setShowImpAnswerCheckerPrompt] = useState(true);

  // Batch modal state
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState('');
  const [batchChallenges, setBatchChallenges] = useState([]); // array of ids
  const [batchLoadError, setBatchLoadError] = useState('');
  const [batchLoading, setBatchLoading] = useState(false);
  const [challengeStatuses, setChallengeStatuses] = useState({}); // id -> status

  // Challenge history state
  const [selectedChallengeId, setSelectedChallengeId] = useState('');
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [challengeLoading, setChallengeLoading] = useState(false);
  const [challengeError, setChallengeError] = useState('');
  const [challengeStatus, setChallengeStatus] = useState('');
  const [hasDevAccess, setHasDevAccess] = useState(false);
  const [isCheckingDevAccess, setIsCheckingDevAccess] = useState(true);

  // Safe stringify helpers for rendering possibly object values
  const toDisplayString = (val) => {
    if (val == null) return '';
    if (typeof val === 'string') return val;
    try {
      return JSON.stringify(val, null, 2);
    } catch (e) {
      try {
        return String(val);
      } catch {
        return '';
      }
    }
  };
  const toInlineString = (val, maxLen = 200) => {
    const s = toDisplayString(val).replace(/\s+/g, ' ').trim();
    return s.length > maxLen ? s.slice(0, maxLen) + '…' : s;
  };

  const formatTimestamp = (value) => {
    if (!value) return '';
    try {
      if (typeof value === 'string') {
        const parsed = new Date(value);
        if (!Number.isNaN(parsed.getTime())) return parsed.toLocaleString();
        return value;
      }
      if (typeof value === 'number') {
        const parsed = new Date(value);
        if (!Number.isNaN(parsed.getTime())) return parsed.toLocaleString();
      }
      if (value instanceof Date) {
        if (!Number.isNaN(value.getTime())) return value.toLocaleString();
      }
      if (typeof value === 'object') {
        if (typeof value.toDate === 'function') {
          return value.toDate().toLocaleString();
        }
        if (typeof value.seconds === 'number') {
          return new Date(value.seconds * 1000).toLocaleString();
        }
      }
    } catch (e) {
      return '';
    }
    return '';
  };

  const formatPercent = (value) => {
    if (value == null || Number.isNaN(value)) return '—';
    return percent(value);
  };

  const computePairKey = (row) => {
    if (!row || typeof row !== 'object') return '';
    const candidates = [
      'pair_path',
      'question_pair_path',
      'question_path',
      'pairKey',
      'pair_key',
      'canonical_pair_path',
      'doc_path',
      'docId',
      'doc_id',
      'result_path',
      'result_doc_path',
      'question_id',
      'command_id',
    ];
    for (const key of candidates) {
      const value = row[key];
      if (typeof value === 'string' && value.trim().length > 0) {
        return value.trim();
      }
    }
    if (row.question && row.command) {
      return `${String(row.question)}||${String(row.command)}`;
    }
    if (row.question) return String(row.question);
    if (row.id) return String(row.id);
    return '';
  };

  const getQuestionKey = (row) => {
    if (!row || typeof row !== 'object') return '';
    if (row.id != null) return String(row.id);
    if (typeof row.firestorePath === 'string' && row.firestorePath.trim().length > 0) {
      return row.firestorePath.trim();
    }
    return computePairKey(row);
  };

  const totalBatchesAcrossTest = useMemo(() => {
    if (!Array.isArray(checkpoints)) return 0;
    return checkpoints.length;
  }, [checkpoints]);

  const summaryStats = useMemo(() => {
    if (!Array.isArray(checkpoints) || checkpoints.length === 0) {
      return {
        totalCheckpoints: 0,
        overallAccuracy: null,
        latestAccuracy: null,
        totalBatches: totalBatchesAcrossTest,
        lastUpdated: '',
      };
    }

    let overallCorrect = 0;
    let overallTotal = 0;
    checkpoints.forEach((cp) => {
      Object.values(cp?.per_batch || {}).forEach((row) => {
        if (!row || !row.total_questions) return;
        overallCorrect += Number(row.correct_questions || 0);
        overallTotal += Number(row.total_questions || 0);
      });
    });

    const latestCheckpoint = checkpoints[checkpoints.length - 1];
    let latestCorrect = 0;
    let latestTotal = 0;
    Object.values(latestCheckpoint?.per_batch || {}).forEach((row) => {
      if (!row || !row.total_questions) return;
      latestCorrect += Number(row.correct_questions || 0);
      latestTotal += Number(row.total_questions || 0);
    });

    const totalBatches =
      totalBatchesAcrossTest > 0
        ? totalBatchesAcrossTest
        : Object.keys(latestCheckpoint?.per_batch || {}).length;

    return {
      totalCheckpoints: checkpoints.length,
      overallAccuracy: overallTotal > 0 ? overallCorrect / overallTotal : null,
      latestAccuracy: latestTotal > 0 ? latestCorrect / latestTotal : null,
      totalBatches,
      lastUpdated:
        formatTimestamp(
          latestCheckpoint?.created_at ||
            latestCheckpoint?.timestamp ||
            latestCheckpoint?.updated_at
        ) || '',
    };
  }, [checkpoints, totalBatchesAcrossTest]);

  const buildComparisonDescriptor = (row) => {
    if (!row || typeof row !== 'object') return null;
    const runSource = row.checkpoint_num ?? row.checkpoint_batch ?? selectedCheckpointForQuestions ?? '';
    const testSource = row.test_id || selectedTest || '';
    const batchSource = row.batch_num ?? selectedBatchForQuestions ?? '';

    let total = row.total_batches;
    if (total == null && row.total_batches_count != null) total = row.total_batches_count;
    if (total == null && row.per_batch_count != null) total = row.per_batch_count;
    const coercedTotal = Number(total) || 0;
    const totalBatches = totalBatchesAcrossTest > 0 ? totalBatchesAcrossTest : coercedTotal;

    const runId = String(runSource || '').trim();
    const testId = String(testSource || '').trim();
    const batch = String(batchSource || '').trim();

    const firestorePath = typeof row.firestorePath === 'string' ? row.firestorePath.trim() : '';
    let pathIntegrationId = '';
    let pathQaId = '';
    if (firestorePath) {
      const segments = firestorePath.split('/').filter(Boolean);
      const integrationIdx = segments.indexOf('integrations');
      if (integrationIdx !== -1 && segments[integrationIdx + 1]) {
        pathIntegrationId = segments[integrationIdx + 1];
      }
      const answersIdx = segments.indexOf('answers');
      if (answersIdx !== -1 && segments[answersIdx + 1]) {
        pathQaId = segments[answersIdx + 1];
      }
    }

    const integrationCandidates = [
      row.integration_id,
      row.integrationId,
      row.integration,
      selectedIntegration,
      pathIntegrationId,
    ];
    const qaCandidates = [
      row.qa_id,
      row.qaId,
      row.qa_pair_id,
      row.qaPairId,
      row.question_id,
      row.command_id,
      row.doc_id,
      row.docId,
      row.id,
      pathQaId,
    ];

    const integrationId = integrationCandidates
      .map((val) => (typeof val === 'string' || typeof val === 'number' ? String(val).trim() : ''))
      .find((val) => val.length);
    const qaId = qaCandidates
      .map((val) => (typeof val === 'string' || typeof val === 'number' ? String(val).trim() : ''))
      .find((val) => val.length);

    if (!runId || !testId || !batch) return null;

    return {
      run_id: runId,
      test_id: testId,
      batch,
      total_batches: totalBatches,
      ...(qaId ? { qa_id: qaId } : {}),
      ...(integrationId
        ? { integration_id: integrationId, integration: integrationId }
        : {}),
    };
  };

  // Simple collapsible section helper
  const Collapsible = ({ title, open, onToggle, children, right }) => (
    <div className="border rounded">
      <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50">
        <div className="text-sm font-medium">{title}</div>
        <div className="flex items-center gap-2">
          {right}
          <button
            className="text-xs px-2 py-1 rounded border hover:bg-white"
            onClick={onToggle}
          >
            {open ? 'Hide' : 'Show'}
          </button>
        </div>
      </div>
      {open && <div className="p-3">{children}</div>}
    </div>
  );

  // Questions modal state
  const [showQuestionsModal, setShowQuestionsModal] = useState(false);
  const [selectedBatchForQuestions, setSelectedBatchForQuestions] = useState('');
  const [selectedCheckpointForQuestions, setSelectedCheckpointForQuestions] = useState('');
  const [questions, setQuestions] = useState([]); // results docs
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [questionsError, setQuestionsError] = useState('');
  const [questionAnalyses, setQuestionAnalyses] = useState({}); // questionKey -> { status, data?, error? }
  const [selectedQuestionIdx, setSelectedQuestionIdx] = useState(null);
  const [showQuestionInternals, setShowQuestionInternals] = useState(false);
  const [showQQuestion, setShowQQuestion] = useState(true);
  const [showQCommand, setShowQCommand] = useState(true);
  const [showQExpected, setShowQExpected] = useState(true);
  const [showQReceived, setShowQReceived] = useState(true);
  const [showQEvaluation, setShowQEvaluation] = useState(true);
  const [analysisWindow, setAnalysisWindow] = useState({ open: false, text: '', title: '' });

  // Batch challenge detail collapsibles
  const [showChallengeScreenshot, setShowChallengeScreenshot] = useState(true);
  const [showChallengeActivity, setShowChallengeActivity] = useState(true);
  const [showChallengeSystemPrompt, setShowChallengeSystemPrompt] = useState(true);
  const [showScreenshotLightbox, setShowScreenshotLightbox] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const verifyDevAccess = async () => {
      setIsCheckingDevAccess(true);
      try {
        const uid = await getUserId();
        if (!uid) {
          if (!cancelled) {
            setHasDevAccess(false);
          }
          return;
        }

        const allowed = await canAccessDevs(uid);
        if (!cancelled) {
          setHasDevAccess(Boolean(allowed));
        }
      } catch (error) {
        if (!cancelled) {
          setHasDevAccess(false);
        }
      } finally {
        if (!cancelled) {
          setIsCheckingDevAccess(false);
        }
      }
    };

    verifyDevAccess();
    return () => {
      cancelled = true;
    };
  }, []);

  const requireDevAccess = useCallback(
    (setError) => {
      if (isCheckingDevAccess) {
        if (typeof setError === 'function') {
          setError(DEV_CHECKING_MESSAGE);
        }
        return false;
      }
      if (!hasDevAccess) {
        if (typeof setError === 'function') {
          setError(DEV_RESTRICTED_MESSAGE);
        }
        return false;
      }
      return true;
    },
    [isCheckingDevAccess, hasDevAccess]
  );

  useEffect(() => {
    if (!isCheckingDevAccess && hasDevAccess) {
      setImprovementsError((prev) =>
        prev === DEV_CHECKING_MESSAGE || prev === DEV_RESTRICTED_MESSAGE ? '' : prev
      );
      setQuestionsError((prev) =>
        prev === DEV_CHECKING_MESSAGE || prev === DEV_RESTRICTED_MESSAGE ? '' : prev
      );
    }
  }, [hasDevAccess, isCheckingDevAccess]);

  const isRerunnableStatus = (status) => {
    if (!status) return false;
    const normalized = String(status).toLowerCase().replace(/[\s-]+/g, '_');
    // Allow rerun unless the challenge is still active/in-flight
    return !/(pending|running|in_progress|processing|active|queued)/.test(normalized);
  };

  // Rerun modal state (reuse new challenge popup UX)
  const [showRerunModal, setShowRerunModal] = useState(false);
  const [rerunUploading, setRerunUploading] = useState(false);
  const [rerunError, setRerunError] = useState('');
  const [rerunSuccess, setRerunSuccess] = useState('');
  const [rerunImage, setRerunImage] = useState(null);
  const [rerunImagePreview, setRerunImagePreview] = useState(null);
  const [rerunGuidance, setRerunGuidance] = useState('');
  const [rerunMaxTurns, setRerunMaxTurns] = useState('');
  const [rerunSystemPrompt, setRerunSystemPrompt] = useState('');
  const [workgroups, setWorkgroups] = useState([]);
  const [selectedWorkgroup, setSelectedWorkgroup] = useState('');
  const [rerunIntegration, setRerunIntegration] = useState('');

  // Close screenshot lightbox with Escape key
  useEffect(() => {
    if (!showScreenshotLightbox) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setShowScreenshotLightbox(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showScreenshotLightbox]);

  // Helper to fetch statuses for a list of challenge IDs
  const fetchStatusesForIds = async (ids) => {
    if (!ids || !ids.length) return {};
    const statusMap = {};
    // First try display_challenges docs for quick status
    await Promise.all(
      ids.map(async (cid) => {
        try {
          const challenge = await fetchReducedDisplayChallengeById(
            selectedIntegration,
            cid
          );
          if (challenge?.status) {
            statusMap[cid] = String(challenge.status).toLowerCase();
          }
        } catch (_) {}
      })
    );
    // For remaining without status, fetch via API in parallel (best-effort)
    const missing = ids.filter((cid) => !statusMap[cid]);
    if (missing.length) {
      await Promise.all(
        missing.map(async (cid) => {
          try {
            const r = await getChallengeStatus(cid);
            const s = r?.status || r?.state || r?.step || r?.challenge_status;
            if (s) statusMap[cid] = String(s).toLowerCase();
          } catch (_) {}
        })
      );
    }
    return statusMap;
  };

  useEffect(() => {
    const loadIntegrations = async () => {
      try {
        // Try admin REST route first
        console.log('CDDIResults: requesting REST route /api/server/v1/integrations');
        const data = await listIntegrations();
        if (Array.isArray(data) && data.length) {
          setIntegrations(data);
          return;
        }
      } catch (e) {
        // fall through to Firestore fallback
      }
      // Fallback: read from Firestore directly
      try {
        console.log('CDDIResults: requesting Firestore path integrations');
        const list = await fetchIntegrationsCollection();
        setIntegrations(list);
      } catch (e) {
        setIntegrations([]);
      }
    };
    loadIntegrations();
  }, []);

  const loadTests = async (integrationId) => {
    setTests([]);
    setSelectedTest('');
    setCheckpoints([]);
    setError('');
    if (!integrationId) return;
    try {
      console.log(`CDDIResults: requesting Firestore path integrations/${integrationId}/tests`);
      const list = await fetchIntegrationTests(integrationId);
      console.log(`CDDIResults: retrieved ${list.length} tests`);
      setTests(list);
    } catch (e) {
      setError('Failed to load tests');
    }
  };

  const loadCheckpoints = async (integrationId, testId) => {
    setCheckpoints([]);
    setImprovements([]);

    setError('');
    if (!integrationId || !testId) return;
    setLoading(true);
    try {
      console.log(`CDDIResults: requesting Firestore path integrations/${integrationId}/tests/${testId}/checkpoints`);
      const list = await fetchIntegrationCheckpoints(integrationId, testId);
      console.log(`CDDIResults: retrieved ${list.length} checkpoints`);
      setCheckpoints(list);
    } catch (e) {
      setError('Failed to load checkpoints');
    } finally {
      setLoading(false);
    }
  };

  const loadImprovements = async (integrationId, testId) => {
    setImprovements([]);
    setImprovementsError('');
    setImprovementsLoading(true);
    try {
      const list = await fetchIntegrationImprovements(integrationId, testId);
      setImprovements(list);
      setSelectedImprovementIdx(0);
    } catch (e) {
      setImprovementsError('Failed to load improvements');
    } finally {
      setImprovementsLoading(false);
    }
  };


  const onPickIntegration = (id) => {
    setSelectedIntegration(id);
    loadTests(id);
  };

  const onPickTest = (id) => {
    setSelectedTest(id);
    loadCheckpoints(selectedIntegration, id);
    loadImprovements(selectedIntegration, id);
  };

  const openBatchModal = async (batchId) => {
    if (!selectedIntegration || !selectedTest || !batchId) return;
    setSelectedBatch(String(batchId));
    setShowBatchModal(true);
    setBatchChallenges([]);
    setSelectedChallengeId('');
    setSelectedChallenge(null);
    setShowChallengeScreenshot(true);
    setShowChallengeActivity(true);
    setShowChallengeSystemPrompt(true);
    setShowScreenshotLightbox(false);
    setBatchLoadError('');
    setChallengeError('');
    setBatchLoading(true);
    try {
      const data = await fetchIntegrationBatchDetails(
        selectedIntegration,
        selectedTest,
        batchId
      );
      const ids = Array.isArray(data?.challenge_ids) ? data.challenge_ids : [];
      setBatchChallenges(ids);
      setChallengeStatuses({});
      if (!ids.length) setBatchLoadError('No challenges recorded for this batch');
      // Kick off status fetch for these ids
      const statusMap = await fetchStatusesForIds(ids);
      if (Object.keys(statusMap).length) setChallengeStatuses(statusMap);
    } catch (e) {
      setBatchLoadError('Failed to load batch challenges');
    } finally {
      setBatchLoading(false);
    }
  };

  // Load workgroups when rerun modal opens
  useEffect(() => {
    if (!showRerunModal) return;
    (async () => {
      try {
        const userId = await getUserId();
        if (!userId) return;
        const list = await fetchWorkgroupDetails(userId);
        setWorkgroups(list);
        if (list.length) setSelectedWorkgroup((v) => v || list[0].id);
      } catch (_) {}
    })();
  }, [showRerunModal]);

  // Rerun helpers
  const onPickRerunImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setRerunError('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setRerunError('Image file size must be less than 5MB');
      return;
    }
    setRerunImage(file);
    const reader = new FileReader();
    reader.onload = (ev) => setRerunImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const openRerun = async (options = {}) => {
    const systemPromptOverride =
      options && typeof options.systemPrompt === 'string'
        ? options.systemPrompt.trim()
        : '';
    try {
      const chunks = Array.isArray(selectedChallenge?.chunks) ? selectedChallenge.chunks : [];
      await deleteChunks(chunks);
    } catch (e) {
      console.warn('deleteChunks error (ignored for now):', e);
    }
    setRerunError('');
    setRerunSuccess('');
    // Prefill guidance/maxTurns from original
    setRerunGuidance(selectedChallenge?.guidance || '');
    setRerunMaxTurns(
      selectedChallenge?.max_turns != null && !Number.isNaN(Number(selectedChallenge.max_turns))
        ? String(selectedChallenge.max_turns)
        : ''
    );
    // Prefill image from original if available
    try {
      const b64 = selectedChallenge?.image_base64;
      if (b64 && typeof b64 === 'string' && b64.length > 0) {
        const byteString = atob(b64);
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
        const blob = new Blob([ab], { type: 'image/jpeg' });
        const file = new File([blob], `challenge_${selectedChallengeId}.jpg`, { type: 'image/jpeg' });
        setRerunImage(file);
        setRerunImagePreview(`data:image/jpeg;base64,${b64}`);
      } else {
        setRerunImage(null);
        setRerunImagePreview(null);
      }
    } catch (_) {
      setRerunImage(null);
      setRerunImagePreview(null);
    }
    // Prefill integration/workgroup
    setRerunIntegration((selectedChallenge?.integration_id) || selectedIntegration || '');
    if (selectedChallenge?.workgroup_id) setSelectedWorkgroup(selectedChallenge.workgroup_id);
    setRerunSystemPrompt(systemPromptOverride);
    setShowRerunModal(true);
  };

  const submitRerun = async () => {
    if (!rerunIntegration) {
      setRerunError('Please select an integration');
      return;
    }
    if (!selectedWorkgroup) {
      setRerunError('Please select a workgroup');
      return;
    }
    if (!rerunImage && (!rerunGuidance || rerunGuidance.trim().length === 0)) {
      setRerunError('Please add a screenshot or guidance');
      return;
    }
    const parsedMax = rerunMaxTurns !== '' ? parseInt(rerunMaxTurns, 10) : null;
    if (rerunMaxTurns !== '' && (isNaN(parsedMax) || parsedMax <= 0)) {
      setRerunError('Max turns must be a positive integer');
      return;
    }
    setRerunError('');
    setRerunUploading(true);
    try {
      await uploadChallenge(
        rerunIntegration,
        rerunImage,
        selectedWorkgroup,
        rerunGuidance,
        parsedMax ?? null,
        rerunSystemPrompt,
      );
      setRerunSuccess('Challenge submitted');
      setShowRerunModal(false);
      setRerunSystemPrompt('');
    } catch (e) {
      setRerunError(e?.message || 'Failed to submit');
    } finally {
      setRerunUploading(false);
    }
  };

  const loadChallengeDetails = async (challengeId) => {
    if (!challengeId || !selectedIntegration) return;
    setSelectedChallengeId(challengeId);
    setSelectedChallenge(null);
    setShowChallengeScreenshot(true);
    setShowChallengeActivity(true);
    setShowChallengeSystemPrompt(true);
    setShowScreenshotLightbox(false);
    setChallengeError('');
    setChallengeLoading(true);
    setChallengeStatus('');
    try {
      // Prefer the full display challenge payload; fall back to the reduced preview when needed
      let displayChallenge = null;
      try {
        displayChallenge = await fetchIntegrationDisplayChallenge(
          selectedIntegration,
          challengeId
        );
      } catch (_) {
        // Ignore and fall back to the reduced doc
      }
      if (!displayChallenge) {
        displayChallenge = await fetchReducedDisplayChallengeById(
          selectedIntegration,
          challengeId
        );
      }
      if (displayChallenge) {
        setSelectedChallenge(displayChallenge);
        if (displayChallenge.status) {
          setChallengeStatus(String(displayChallenge.status));
          setChallengeStatuses((prev) => ({
            ...prev,
            [challengeId]: String(displayChallenge.status).toLowerCase(),
          }));
        }
        // continue to also fetch status
      }
      // Fallback to challenge activity API
      const act = await getChallengeActivity(challengeId);
      setSelectedChallenge((prev) => ({
        ...(prev || { id: challengeId }),
        id: challengeId,
        activity_history: act?.activity_history || act || [],
      }));

      // Try to derive status from activity response if present
      try {
        const actStatus = act?.status || act?.state || act?.step || (typeof act === 'object' ? act?.challenge_status : '');
        if (actStatus) setChallengeStatus(String(actStatus));
      } catch {}
    } catch (e) {
      setChallengeError('Failed to load challenge history');
    } finally {
      setChallengeLoading(false);
    }

    // Independently fetch challenge status for a definitive final state
    try {
      const statusResp = await getChallengeStatus(challengeId);
      const s = statusResp?.status || statusResp?.state || statusResp?.step || statusResp?.challenge_status;
      if (s) {
        setChallengeStatus(String(s));
        setChallengeStatuses((prev) => ({ ...prev, [challengeId]: String(s).toLowerCase() }));
      }
    } catch (e) {
      // Non-fatal; keep whatever we have
    }
  };

  const openQuestionsModal = async (batchId, checkpointId) => {
    if (!selectedIntegration || !selectedTest || !batchId) return;
    setSelectedBatchForQuestions(String(batchId));
    setSelectedCheckpointForQuestions(checkpointId ? String(checkpointId) : '');
    setShowQuestionsModal(true);
    setQuestions([]);
    setQuestionAnalyses({});
    setSelectedQuestionIdx(null);
    setShowQuestionInternals(false);
    setShowQQuestion(true);
    setShowQCommand(true);
    setShowQExpected(true);
    setShowQReceived(true);
    setShowQEvaluation(true);
    if (!requireDevAccess(setQuestionsError)) {
      setQuestionsLoading(false);
      return;
    }
    setQuestionsError('');
    setQuestionsLoading(true);
    try {
      const list = await fetchBatchAnswers(
        selectedIntegration,
        selectedTest,
        batchId,
        checkpointId
      );
      const processedList = Array.isArray(list)
        ? list.map((row) => ({
            ...row,
            firestorePath: typeof row.firestorePath === 'string' ? row.firestorePath.trim() : '',
          }))
        : [];
      setQuestions(processedList);
      if (!processedList.length) {
        setQuestionsError(`No answers found for batch ${batchId}${checkpointId ? ` @ checkpoint ${checkpointId}` : ''}`);
      }
    } catch (e) {
      setQuestionsError('Failed to load answers');
    } finally {
      setQuestionsLoading(false);
    }
  };

  const handleCompareQuestion = async (question) => {
    if (!question) return;
    const questionKey = getQuestionKey(question);
    const targetDescriptor = buildComparisonDescriptor(question);
    if (!questionKey || !targetDescriptor) return;
    if (!requireDevAccess(setQuestionsError)) {
      return;
    }
    setQuestionsError('');

    setQuestionAnalyses((prev) => ({
      ...prev,
      [questionKey]: { status: 'loading' },
    }));

    try {
      const pairKey = computePairKey(question) || questionKey;
      const seen = new Set();
      const comparisons = [];

      (questions || []).forEach((q) => {
        const compKey = getQuestionKey(q);
        if (!compKey || compKey === questionKey) return;
        const compPairKey = computePairKey(q) || compKey;
        if (compPairKey !== pairKey) return;
        const descriptor = buildComparisonDescriptor(q);
        if (!descriptor) return;
        const dedupeKey = `${descriptor.run_id}::${descriptor.batch}::${descriptor.test_id}`;
        if (seen.has(dedupeKey)) return;
        seen.add(dedupeKey);
        comparisons.push(descriptor);
      });

      const response = await analyseTestQuestionDifference({
        target: targetDescriptor,
        comparisons,
      });

      setQuestionAnalyses((prev) => ({
        ...prev,
        [questionKey]: { status: 'success', data: response },
      }));

      const previewTitle = toInlineString(question?.question || 'Question analysis', 80);
      const responseText =
        typeof response === 'string' ? response : toDisplayString(response);
      setAnalysisWindow({ open: true, text: responseText, title: previewTitle });
    } catch (err) {
      setQuestionAnalyses((prev) => ({
        ...prev,
        [questionKey]: {
          status: 'error',
          error: err?.message || 'Failed to compare runs.',
        },
      }));
    }

  };

  const renderNarrative = () => {
    if (!checkpoints.length) return null;
    const lines = [];
    checkpoints.forEach((cp) => {
      const b = cp.per_batch || {};
      const entries = Object.keys(b)
        .sort((a, b) => Number(a) - Number(b))
        .map((k) => {
          const row = b[k] || {};
          const pct = row.total_questions ? row.correct_questions / row.total_questions : 0;
          return `batch ${k} got ${percent(pct)}`;
        })
        .join(', ');
      lines.push(`After batch ${cp.checkpoint_batch || cp.id} was trained ${entries}.`);
    });
    return (
      <div className="space-y-2 text-sm leading-relaxed text-foreground/80">
        {lines.map((t, i) => (
          <div key={i}>{t}</div>
        ))}
      </div>
    );
  };

  const renderTable = () => {
    if (!checkpoints.length) return null;
    // Collect all batch columns
    const colsSet = new Set();
    checkpoints.forEach((cp) => Object.keys(cp.per_batch || {}).forEach((k) => colsSet.add(k)));
    const cols = Array.from(colsSet).sort((a, b) => Number(a) - Number(b));
    return (
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="min-w-full text-sm">
          <thead className="bg-muted/70">
            <tr>
              <th className="px-4 py-2 text-left font-medium text-muted-foreground">
                Checkpoint
              </th>
              {cols.map((c) => (
                <th key={c} className="px-4 py-2 text-left font-medium text-muted-foreground">
                  <button
                    type="button"
                    className="font-medium text-primary transition-colors hover:text-primary/80"
                    onClick={() => openBatchModal(c)}
                    title="View challenges for this batch"
                  >
                    Batch {c}
                  </button>
                </th>
              ))}
              <th className="px-4 py-2 text-left font-medium text-muted-foreground">Average</th>
            </tr>
          </thead>
          <tbody>
            {checkpoints.map((cp) => (
              <tr key={cp.id} className="border-t border-border">
                <td className="px-4 py-2 font-medium text-foreground">
                  {cp.checkpoint_batch || cp.id}
                </td>
                {cols.map((c) => {
                  const row = (cp.per_batch || {})[c];
                  const pct = row && row.total_questions ? row.correct_questions / row.total_questions : 0;
                  const hasData = !!row;
                  return (
                    <td key={c} className="px-0 py-0">
                      <button
                        type="button"
                        className={`w-full h-full px-4 py-2 text-left transition-colors ${
                          hasData ? 'hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring' : ''
                        }`}
                        onClick={() => openQuestionsModal(c, cp.checkpoint_batch || cp.id)}
                        title={hasData ? 'View questions for this batch' : 'No data'}
                      >
                        {hasData ? `${percent(pct)} (${row.correct_questions}/${row.total_questions})` : '—'}
                      </button>
                    </td>
                  );
                })}
                {(() => {
                  const batches = Object.values(cp.per_batch || {});
                  const sums = batches.reduce(
                    (acc, r) => {
                      if (r && r.total_questions) {
                        acc.correct += Number(r.correct_questions || 0);
                        acc.total += Number(r.total_questions || 0);
                      }
                      return acc;
                    },
                    { correct: 0, total: 0 }
                  );
                  const avgPct = sums.total ? sums.correct / sums.total : 0;
                  return (
                    <td className="px-4 py-2 font-medium text-foreground">
                      {`${percent(avgPct)} (${sums.correct}/${sums.total})`}
                    </td>
                  );
                })()}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Register ChartJS components once
  ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

  const renderChart = () => {
    if (!checkpoints.length) return null;
    // Gather labels and series keys
    const labels = checkpoints.map((cp) => String(cp.checkpoint_batch || cp.id));
    const colsSet = new Set();
    checkpoints.forEach((cp) => Object.keys(cp.per_batch || {}).forEach((k) => colsSet.add(k)));
    const cols = Array.from(colsSet).sort((a, b) => Number(a) - Number(b));

    // Build datasets: one line per batch column
    const datasets = cols.map((c, idx) => {
      const data = checkpoints.map((cp) => {
        const row = (cp.per_batch || {})[c];
        if (!row || !row.total_questions) return null; // gap if missing
        const pct = row.correct_questions / row.total_questions;
        return Math.round(pct * 100);
      });
      const hue = Math.round((idx * 360) / Math.max(cols.length, 1));
      const color = `hsl(${hue} 70% 45%)`;
      const colorFill = `hsl(${hue} 70% 45% / 0.2)`;
      return {
        label: `Batch ${c}`,
        data,
        borderColor: color,
        backgroundColor: colorFill,
        pointRadius: 3,
        tension: 0.25,
        spanGaps: false,
      };
    });

    // Average line per checkpoint (weighted by total_questions)
    const avgData = checkpoints.map((cp) => {
      const batches = Object.values(cp.per_batch || {});
      const sums = batches.reduce(
        (acc, r) => {
          if (r && r.total_questions) {
            acc.correct += Number(r.correct_questions || 0);
            acc.total += Number(r.total_questions || 0);
          }
          return acc;
        },
        { correct: 0, total: 0 }
      );
      if (!sums.total) return null; // gap if no data
      return Math.round((sums.correct / sums.total) * 100);
    });

    datasets.push({
      label: 'Average',
      data: avgData,
      borderColor: '#111827', // gray-900
      backgroundColor: 'rgba(17,24,39,0.15)',
      borderWidth: 3,
      borderDash: [6, 4],
      pointRadius: 3,
      tension: 0.2,
      spanGaps: false,
      order: 99,
    });

    const data = { labels, datasets };
    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'top' },
        title: { display: true, text: 'Batch Accuracy by Checkpoint' },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const v = ctx.parsed.y;
              if (v == null || Number.isNaN(v)) return `${ctx.dataset.label}: -`;
              return `${ctx.dataset.label}: ${v}%`;
            },
          },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          max: 100,
          ticks: {
            callback: (val) => `${val}%`,
          },
          title: { display: true, text: 'Accuracy' },
        },
        x: {
          title: { display: true, text: 'Checkpoint' },
        },
      },
    };

    return (
      <div className="w-full h-80">
        <Line data={data} options={options} />
      </div>
    );
  };

  const renderImprovements = () => {
    if (improvementsLoading) {
      return <div className="text-sm text-muted-foreground">Loading improvements…</div>;
    }
    if (improvementsError) {
      return (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-2 text-sm text-destructive">
          {improvementsError}
        </div>
      );
    }
    if (isCheckingDevAccess) {
      return <div className="text-sm text-muted-foreground">{DEV_CHECKING_MESSAGE}</div>;
    }
    if (!hasDevAccess) {
      return (
        <div className="rounded-md border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
          {DEV_RESTRICTED_MESSAGE}
        </div>
      );
    }
    if (!improvements.length) {
      return <div className="text-sm text-muted-foreground">No improvements recorded yet.</div>;
    }

    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Select a recommendation to inspect analysis, prompts, and guidance.
        </p>
        <ul className="space-y-2">
          {improvements.map((imp, i) => {
            const created = (() => {
              try {
                if (imp.created_at && imp.created_at.toDate) return imp.created_at.toDate().toLocaleString();
                if (imp.created_at instanceof Date) return imp.created_at.toLocaleString();
                if (typeof imp.created_at === 'string') return imp.created_at;
              } catch {}
              return '';
            })();
            return (
              <li key={imp.id}>
                <button
                  className="w-full text-left rounded-lg border border-border bg-background px-4 py-3 transition-colors hover:border-primary/40 hover:bg-accent/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  onClick={() => {
                    if (!requireDevAccess(setImprovementsError)) {
                      return;
                    }
                    setImprovementsError('');
                    setSelectedImprovementIdx(i);
                    setShowImprovementInternals(false);
                    // Reset modal section toggles on open
                    setShowImpAnalysis(true);
                    setShowImpGenericInstructions(true);
                    setShowImpGenericLearningPrompt(true);
                    setShowImpIntegrationLearningPrompt(true);
                    setShowImpAnswerCheckerPrompt(true);
                    setShowImprovementModal(true);
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-sm text-foreground">{imp.id}</div>
                    {created && <div className="text-xs text-muted-foreground">{created}</div>}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  return (
    <div className="space-y-8 px-6 pb-16" data-testid="cddi-results-page">
      <div className="space-y-2">
        <h1 className="text-3xl font-semibold tracking-tight">CDDI Results</h1>
        <p className="text-muted-foreground">
          Monitor how autonomous training improves accuracy across checkpoints and batches.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Card className="border-primary/20 bg-primary/5">
          <CardHeader className="pb-2">
            <CardDescription>Total Checkpoints</CardDescription>
            <CardTitle className="text-3xl font-semibold">
              {summaryStats.totalCheckpoints || 0}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 text-sm text-muted-foreground">
            Recorded runs for the selected test.
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Overall Accuracy</CardDescription>
            <CardTitle className="text-3xl font-semibold">
              {summaryStats.overallAccuracy != null
                ? formatPercent(summaryStats.overallAccuracy)
                : '—'}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 text-sm text-muted-foreground">
            Weighted across all batches in the test.
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Latest Checkpoint</CardDescription>
            <CardTitle className="text-3xl font-semibold">
              {summaryStats.latestAccuracy != null
                ? formatPercent(summaryStats.latestAccuracy)
                : '—'}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 text-sm text-muted-foreground">
            {summaryStats.lastUpdated
              ? `Captured ${summaryStats.lastUpdated}`
              : 'Awaiting new checkpoint data.'}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Batches Tracked</CardDescription>
            <CardTitle className="text-3xl font-semibold">
              {summaryStats.totalBatches || 0}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 text-sm text-muted-foreground">
            Unique batch progress across checkpoints.
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-0">
          <CardTitle className="text-xl">Select Test Run</CardTitle>
          <CardDescription>
            Choose an integration and test to explore performance.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 pt-6 md:grid-cols-2">
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Integration</label>
            {Array.isArray(integrations) && integrations.length > 0 ? (
              <Select
                value={selectedIntegration}
                onChange={(e) => onPickIntegration(e.target.value)}
              >
                <option value="">Select an integration</option>
                {integrations.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.name || i.id}
                  </option>
                ))}
              </Select>
            ) : (
              <input
                type="text"
                placeholder="Enter integration id"
                value={selectedIntegration}
                onChange={(e) => onPickIntegration(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
            )}
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Test</label>
            <Select
              value={selectedTest}
              onChange={(e) => onPickTest(e.target.value)}
              disabled={!selectedIntegration}
            >
              <option value="">Select a test</option>
              {(Array.isArray(tests) ? tests : []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.id}
                </option>
              ))}
            </Select>
          </div>
        </CardContent>
        {(error || loading) && (
          <CardFooter className="flex flex-col gap-2">
            {error && (
              <div className="w-full rounded-md border border-destructive/40 bg-destructive/10 px-4 py-2 text-sm text-destructive">
                {error}
              </div>
            )}
            {loading && (
              <div className="w-full rounded-md border border-border px-4 py-2 text-sm text-muted-foreground">
                Loading…
              </div>
            )}
          </CardFooter>
        )}
      </Card>

      {!loading && selectedTest && (
        <div className="space-y-6">
          <Card>
            <CardHeader className="pb-0">
              <CardTitle className="text-lg">Accuracy Trends</CardTitle>
              <CardDescription>
                Visualize batch accuracy at each checkpoint.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              {renderChart()}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-0">
              <CardTitle className="text-lg">Narrative Summary</CardTitle>
              <CardDescription>
                Key takeaways generated from checkpoint performance.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              {renderNarrative()}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-0">
              <CardTitle className="text-lg">Checkpoint Breakdown</CardTitle>
              <CardDescription>
                Drill into each batch and open challenges from the run.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              {renderTable()}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-0">
              <CardTitle className="text-lg">Suggested Improvements</CardTitle>
              <CardDescription>
                Review AI recommendations with deep dives available per item.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              {renderImprovements()}
            </CardContent>
          </Card>
        </div>
      )}

      {showImprovementModal && (() => {
        const idx = Math.max(0, Math.min(selectedImprovementIdx, improvements.length - 1));
        const imp = improvements[idx] || {};
        const created = (() => {
          try {
            if (imp.created_at && imp.created_at.toDate) return imp.created_at.toDate().toLocaleString();
            if (imp.created_at instanceof Date) return imp.created_at.toLocaleString();
            if (typeof imp.created_at === 'string') return imp.created_at;
          } catch {}
          return '';
        })();

        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black bg-opacity-40"
              onClick={() => setShowImprovementModal(false)}
            />
            {/* Modal content */}
            <div className="relative bg-white rounded-lg shadow-lg w-full max-w-3xl mx-4 max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <h3 className="text-lg font-semibold">
                  Improvement {imp.id}
                  {created && <span className="ml-2 text-sm font-normal text-gray-500">({created})</span>}
                </h3>
                <button
                  className="px-2 py-1 text-sm text-gray-700 hover:text-gray-900"
                  onClick={() => setShowImprovementModal(false)}
                >
                  Close
                </button>
              </div>
              <div className="p-4 space-y-3">
                {isCheckingDevAccess && (
                  <div className="text-sm text-muted-foreground">{DEV_CHECKING_MESSAGE}</div>
                )}
                {!isCheckingDevAccess && !hasDevAccess && (
                  <div className="rounded-md border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
                    {DEV_RESTRICTED_MESSAGE}
                  </div>
                )}
                {!isCheckingDevAccess && hasDevAccess && (
                  <>
                    {imp.analysis && String(imp.analysis).trim() !== 'null' && (
                      <Collapsible
                        title="Analysis"
                        open={showImpAnalysis}
                        onToggle={() => setShowImpAnalysis((v) => !v)}
                      >
                        <pre className="whitespace-pre-wrap text-xs bg-gray-50 border p-2 rounded">{toDisplayString(imp.analysis)}</pre>
                      </Collapsible>
                    )}
                    {imp.genericInstructions && String(imp.genericInstructions).trim() !== 'null' && (
                      <Collapsible
                        title="Generic Instructions"
                        open={showImpGenericInstructions}
                        onToggle={() => setShowImpGenericInstructions((v) => !v)}
                      >
                        <pre className="whitespace-pre-wrap text-xs bg-gray-50 border p-2 rounded">{toDisplayString(imp.genericInstructions)}</pre>
                      </Collapsible>
                    )}
                    {imp.genericLearningPrompt && String(imp.genericLearningPrompt).trim() !== 'null' && (
                      <Collapsible
                        title="Generic Learning Prompt"
                        open={showImpGenericLearningPrompt}
                        onToggle={() => setShowImpGenericLearningPrompt((v) => !v)}
                      >
                        <pre className="whitespace-pre-wrap text-xs bg-gray-50 border p-2 rounded">{toDisplayString(imp.genericLearningPrompt)}</pre>
                      </Collapsible>
                    )}
                    {imp.integrationLearningPrompt && String(imp.integrationLearningPrompt).trim() !== 'null' && (
                      <Collapsible
                        title="Integration Learning Prompt"
                        open={showImpIntegrationLearningPrompt}
                        onToggle={() => setShowImpIntegrationLearningPrompt((v) => !v)}
                      >
                        <pre className="whitespace-pre-wrap text-xs bg-gray-50 border p-2 rounded">{toDisplayString(imp.integrationLearningPrompt)}</pre>
                      </Collapsible>
                    )}
                    {imp.answerCheckerPrompt && String(imp.answerCheckerPrompt).trim() !== 'null' && (
                      <Collapsible
                        title="Answer Checker Prompt"
                        open={showImpAnswerCheckerPrompt}
                        onToggle={() => setShowImpAnswerCheckerPrompt((v) => !v)}
                      >
                        <pre className="whitespace-pre-wrap text-xs bg-gray-50 border p-2 rounded">{toDisplayString(imp.answerCheckerPrompt)}</pre>
                      </Collapsible>
                    )}
                    {(() => {
                      const msgList = Array.isArray(imp?.internal_messages)
                        ? imp.internal_messages
                        : Array.isArray(imp?.internalMessages)
                        ? imp.internalMessages
                        : Array.isArray(imp?.messages)
                        ? imp.messages
                        : [];
                      return (
                        Array.isArray(msgList) && msgList.length > 0 && (
                          <Collapsible
                            title="Internal Messages"
                            open={showImprovementInternals}
                            onToggle={() => setShowImprovementInternals((v) => !v)}
                          >
                            <div className="max-h-64 overflow-y-auto space-y-2">
                              {msgList.map((m, i) => (
                                <div key={i} className="text-xs">
                                  <div className="text-gray-500 mb-1">{m?.role || 'message'}</div>
                                  <pre className="whitespace-pre-wrap bg-white border p-2 rounded">{toDisplayString(m?.content)}</pre>
                                </div>
                              ))}
                            </div>
                          </Collapsible>
                        )
                      );
                    })()}
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {showBatchModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black bg-opacity-40"
            onClick={() => setShowBatchModal(false)}
          />
          {/* Modal content */}
          <div className="relative bg-white rounded-lg shadow-lg w-full max-w-5xl mx-4">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="text-lg font-semibold">Batch {selectedBatch} Challenges</h3>
              <button
                className="px-2 py-1 text-sm text-gray-700 hover:text-gray-900"
                onClick={() => setShowBatchModal(false)}
              >
                Close
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
              {/* Left: challenge list */}
              <div className="md:col-span-1 border-r max-h-[70vh] overflow-y-auto">
                <div className="p-4">
                  {batchLoading && <div className="text-sm text-gray-600">Loading challenges…</div>}
                  {batchLoadError && <div className="text-sm text-red-600">{batchLoadError}</div>}
                  {!batchLoading && !batchLoadError && (
                    <ul className="space-y-2">
                      {batchChallenges.map((cid) => {
                        const st = (challengeStatuses[cid] || '').toLowerCase();
                        const isFail = /fail/.test(st);
                        const isSuccess = /(complete|success|done)/.test(st);
                        const bg = isFail
                          ? 'bg-red-50 border-red-300 hover:bg-red-100'
                          : isSuccess
                          ? 'bg-green-50 border-green-300 hover:bg-green-100'
                          : 'hover:bg-gray-50 border-gray-300';
                        const ring = selectedChallengeId === cid ? 'ring-2 ring-blue-300' : '';
                        return (
                          <li key={cid}>
                            <button
                              className={`w-full text-left px-3 py-2 rounded border ${bg} ${ring}`}
                              onClick={() => loadChallengeDetails(cid)}
                              title={cid}
                            >
                              <div className="flex items-center justify-between">
                                <div className="text-sm font-medium">{cid.substring(0, 12)}…</div>
                                {st && (
                                  <span className="text-[10px] uppercase tracking-wide text-gray-700">{st}</span>
                                )}
                              </div>
                              <div className="text-xs text-gray-500">View history</div>
                            </button>
                          </li>
                        );
                      })}
                      {batchChallenges.length === 0 && (
                        <li className="text-sm text-gray-600">No challenges for this batch.</li>
                      )}
                    </ul>
                  )}
                </div>
              </div>
              {/* Right: challenge history */}
              <div className="md:col-span-2 max-h-[70vh] overflow-y-auto">
                <div className="p-4">
                  {!selectedChallengeId && (
                    <div className="text-sm text-gray-600">Select a challenge to view its history.</div>
                  )}
                  {challengeLoading && <div className="text-sm text-gray-600">Loading history…</div>}
                  {challengeError && <div className="text-sm text-red-600">{challengeError}</div>}
                  {selectedChallenge && (
                    <ChallengeDetailsPanel challenge={selectedChallenge} onRerun={openRerun} />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rerun Modal (same UX as Challenges upload) */}
      {showRerunModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Upload New Challenge</h3>
              <button
                onClick={() => {
                  setShowRerunModal(false);
                  setRerunSystemPrompt('');
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>

            {rerunError && <div className="mb-3 text-sm text-red-600">{rerunError}</div>}
            {rerunSuccess && <div className="mb-3 text-sm text-green-600">{rerunSuccess}</div>}

            <div className="space-y-6">
              <WorkgroupSelect
                workgroups={workgroups}
                selectedWorkgroup={selectedWorkgroup}
                onSelect={setSelectedWorkgroup}
                label="Workgroup *"
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Integration *</label>
                <select
                  value={rerunIntegration}
                  onChange={(e) => setRerunIntegration(e.target.value)}
                  className="w-full px-3 py-2 text-sm border rounded-md"
                >
                  <option value="">Select an integration</option>
                  {(integrations || []).map((integration) => (
                    <option key={integration.id} value={integration.id}>
                      {integration.name || integration.id}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Image (optional)</label>
                <ImageUploadDropzone
                  onChange={onPickRerunImage}
                  helperText="PNG, JPG, GIF up to 5MB"
                  className="border-gray-300 bg-gray-50 hover:bg-gray-100"
                  icon={<span className="mb-2 text-3xl" role="img" aria-hidden="true">⬆️</span>}
                />
                {rerunImagePreview && (
                  <div className="mt-4">
                    <div className="relative inline-block">
                      <img src={rerunImagePreview} alt="Preview" className="max-w-full max-h-48 rounded-lg border" />
                      <button
                        onClick={() => {
                          setRerunImage(null);
                          setRerunImagePreview(null);
                        }}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full px-2"
                        title="Remove image"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Guidance (optional)</label>
                <textarea
                  value={rerunGuidance}
                  onChange={(e) => setRerunGuidance(e.target.value)}
                  rows={4}
                  placeholder="Optionally steer the AI toward a solution; use alone or with a screenshot."
                  className="w-full px-3 py-2 text-sm border rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">System Prompt (optional)</label>
                <textarea
                  value={rerunSystemPrompt}
                  onChange={(e) => setRerunSystemPrompt(e.target.value)}
                  rows={4}
                  placeholder="Seed the rerun with a system prompt, e.g. the latest assistant recommendation."
                  className="w-full px-3 py-2 text-sm border rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Max Turns (optional)</label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={rerunMaxTurns}
                  onChange={(e) => setRerunMaxTurns(e.target.value)}
                  placeholder="Leave blank for default"
                  className="w-full px-3 py-2 text-sm border rounded-md"
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <button
                  onClick={submitRerun}
                  disabled={
                    rerunUploading || !rerunIntegration || !selectedWorkgroup ||
                    (!rerunImage && (!rerunGuidance || rerunGuidance.trim().length === 0))
                  }
                  className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {rerunUploading ? 'Uploading…' : 'Upload Challenge'}
                </button>
                <button
                  onClick={() => {
                    setShowRerunModal(false);
                    setRerunSystemPrompt('');
                  }}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showQuestionsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black bg-opacity-40"
            onClick={() => setShowQuestionsModal(false)}
          />
          {/* Modal content */}
          <div className="relative bg-white rounded-lg shadow-lg w-full max-w-6xl mx-4">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="text-lg font-semibold">
                Batch {selectedBatchForQuestions}
                {selectedCheckpointForQuestions && (
                  <span>{' '}@ Checkpoint {selectedCheckpointForQuestions}</span>
                )} Questions
              </h3>
              <button
                className="px-2 py-1 text-sm text-gray-700 hover:text-gray-900"
                onClick={() => setShowQuestionsModal(false)}
              >
                Close
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
              {isCheckingDevAccess ? (
                <div className="col-span-1 md:col-span-3 p-4 text-sm text-muted-foreground">
                  {DEV_CHECKING_MESSAGE}
                </div>
              ) : !hasDevAccess ? (
                <div className="col-span-1 md:col-span-3 rounded-md border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
                  {DEV_RESTRICTED_MESSAGE}
                </div>
              ) : (
                <>
                  {/* Left: question list */}
                  <div className="md:col-span-1 border-r max-h-[75vh] overflow-y-auto">
                <div className="p-4">
                  {questionsLoading && <div className="text-sm text-gray-600">Loading questions…</div>}
                  {questionsError && <div className="text-sm text-red-600">{questionsError}</div>}
                  {!questionsLoading && !questionsError && (
                    <ul className="space-y-2">
                      {questions.map((q, idx) => {
                        const similar = q?.evaluation?.similar === true;
                        const bg = similar
                          ? 'bg-green-50 border-green-300 hover:bg-green-100'
                          : 'bg-red-50 border-red-300 hover:bg-red-100';
                        const ring = selectedQuestionIdx === idx ? 'ring-2 ring-blue-300' : '';
                        const qText = toInlineString(q?.question || '(no question)');
                        return (
                          <li key={q.id}>
                            <button
                              className={`w-full text-left px-3 py-2 rounded border ${bg} ${ring}`}
                              onClick={() => {
                                setSelectedQuestionIdx(idx);
                                setShowQuestionInternals(false);
                                setShowQQuestion(true);
                                setShowQCommand(true);
                                setShowQExpected(true);
                                setShowQReceived(true);
                                setShowQEvaluation(true);
                              }}
                              title={qText}
                            >
                              <div className="text-xs text-gray-600 mb-1">{similar ? 'Correct' : 'Incorrect'}</div>
                              <div className="text-sm font-medium line-clamp-2 whitespace-normal">{qText}</div>
                            </button>
                          </li>
                        );
                      })}
                      {questions.length === 0 && (
                        <li className="text-sm text-gray-600">No questions for this batch.</li>
                      )}
                    </ul>
                  )}
                </div>
                  </div>
                  {/* Right: question detail */}
                  <div className="md:col-span-2 max-h-[75vh] overflow-y-auto">
                <div className="p-4 space-y-4">
                  {selectedQuestionIdx == null && (
                    <div className="text-sm text-gray-600">Select a question to view expected and received answers.</div>
                  )}
                  {selectedQuestionIdx != null && questions[selectedQuestionIdx] && (() => {
                    const q = questions[selectedQuestionIdx];
                    const similar = q?.evaluation?.similar === true;
                    return (
                      <div className="space-y-4">
                        {(() => {
                          const questionKey = getQuestionKey(q);
                          const analysis = questionKey ? questionAnalyses[questionKey] : null;
                          const status = analysis?.status;
                          const isLoading = status === 'loading';
                          const descriptor = buildComparisonDescriptor(q);
                          const canCompare = !!descriptor;
                          const analysisData = analysis?.data;
                          const comparisons =
                            analysisData && typeof analysisData === 'object' && Array.isArray(analysisData.comparisons)
                              ? analysisData.comparisons
                              : [];
                          const targetInfo =
                            analysisData && typeof analysisData === 'object' ? analysisData.target : null;
                          const hasTargetDetails =
                            targetInfo && (targetInfo.run_id || targetInfo.test_id || targetInfo.batch);
                          const comparisonCount =
                            analysisData && typeof analysisData === 'object'
                              ? analysisData.comparison_count
                              : null;
                          const analysisText = (() => {
                            if (typeof analysisData === 'string') return analysisData;
                            if (analysisData && typeof analysisData === 'object') {
                              if (analysisData.analysis) return analysisData.analysis;
                              if (analysisData.content) return analysisData.content;
                              return toDisplayString(analysisData);
                            }
                            return 'No analysis returned';
                          })();
                          return (
                            <div className="space-y-2">
                              <div className="flex flex-wrap items-center gap-2">
                                <button
                                  type="button"
                                  className="px-3 py-1 text-xs border rounded bg-white hover:bg-blue-50 disabled:opacity-60 disabled:cursor-not-allowed"
                                  onClick={() => handleCompareQuestion(q)}
                                  disabled={!canCompare || isLoading}
                                >
                                  {isLoading ? 'Comparing…' : 'Compare to other runs'}
                                </button>
                                {!canCompare && (
                                  <span className="text-xs text-gray-500">Comparison metadata unavailable for this result.</span>
                                )}
                              </div>
                              {status === 'error' && analysis?.error && (
                                <div className="text-xs text-red-600">{analysis.error}</div>
                              )}
                              {status === 'success' && (
                                <div className="space-y-2 rounded border border-blue-200 bg-blue-50 p-3 text-xs text-gray-800">
                                  <pre className="whitespace-pre-wrap bg-white/60 border border-blue-100 rounded p-2 text-[11px] sm:text-xs">
                                    {toDisplayString(analysisText)}
                                  </pre>
                                  {typeof comparisonCount === 'number' && (
                                    <div className="text-[11px] text-gray-600">
                                      Compared against {comparisonCount} other run{comparisonCount === 1 ? '' : 's'}.
                                    </div>
                                  )}
                                  {(!comparisons || comparisons.length === 0) && (
                                    <div className="text-[11px] text-gray-600">
                                      No other runs were available for comparison.
                                    </div>
                                  )}
                                  {hasTargetDetails && (
                                    <div className="text-[11px] text-gray-600">
                                      Target run {targetInfo.run_id} · Batch {targetInfo.batch} of {targetInfo.total_batches} · Test {targetInfo.test_id}
                                    </div>
                                  )}
                                  {comparisons.length > 0 && (
                                    <div className="text-[11px] text-gray-600 space-y-1">
                                      <div className="font-semibold text-gray-700">Compared runs:</div>
                                      <div className="space-y-1">
                                        {comparisons.map((comp, idx) => (
                                          <div
                                            key={`${comp?.run_id || 'run'}-${comp?.batch || 'batch'}-${idx}`}
                                            className="truncate"
                                            title={`Run ${comp?.run_id || 'unknown'} · Batch ${comp?.batch || '?'} · Test ${comp?.test_id || 'unknown'} · Total batches ${comp?.total_batches ?? '?'}`}
                                          >
                                            Run {comp?.run_id || '?'} · Batch {comp?.batch || '?'} of {comp?.total_batches ?? '?'} · Test {comp?.test_id || '?'}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                        <Collapsible
                          title="Question"
                          open={showQQuestion}
                          onToggle={() => setShowQQuestion((v) => !v)}
                        >
                          <pre className="whitespace-pre-wrap text-sm bg-gray-50 border p-2 rounded">{toDisplayString(q?.question || '(no question)')}</pre>
                        </Collapsible>
                        {q?.command && (
                          <Collapsible
                            title="Command"
                            open={showQCommand}
                            onToggle={() => setShowQCommand((v) => !v)}
                          >
                            <pre className="whitespace-pre-wrap text-xs bg-gray-50 border p-2 rounded">{toDisplayString(q.command)}</pre>
                          </Collapsible>
                        )}
                        <Collapsible
                          title="Expected (Command Result)"
                          open={showQExpected}
                          onToggle={() => setShowQExpected((v) => !v)}
                        >
                          <pre className="whitespace-pre-wrap text-xs bg-gray-50 border p-2 rounded">{toDisplayString(q?.expected_answer || q?.command_result || '(none)')}</pre>
                        </Collapsible>
                        <Collapsible
                          title="Received (Standard Answer)"
                          open={showQReceived}
                          onToggle={() => setShowQReceived((v) => !v)}
                        >
                          <pre className="whitespace-pre-wrap text-xs bg-gray-50 border p-2 rounded">{toDisplayString(q?.received_answer || q?.standard_answer || '(none)')}</pre>
                        </Collapsible>
                        {q?.evaluation && (
                          <Collapsible
                            title={`Evaluation ${similar ? '(Similar)' : '(Not Similar)'}`}
                            open={showQEvaluation}
                            onToggle={() => setShowQEvaluation((v) => !v)}
                          >
                            <pre className="whitespace-pre-wrap text-xs bg-gray-50 border p-2 rounded">{typeof q.evaluation === 'string' ? q.evaluation : toDisplayString(q.evaluation)}</pre>
                          </Collapsible>
                        )}
                        {Array.isArray(q?.internal_messages) && q.internal_messages.length > 0 && (
                          <Collapsible
                            title="Internal Messages"
                            open={showQuestionInternals}
                            onToggle={() => setShowQuestionInternals((v) => !v)}
                          >
                            <div className="max-h-64 overflow-y-auto space-y-2">
                              {q.internal_messages.map((m, i) => (
                                <div key={i} className="text-xs">
                                  <div className="text-gray-500 mb-1">{m?.role || 'message'}</div>
                                  <pre className="whitespace-pre-wrap bg-white border p-2 rounded">{toDisplayString(m?.content)}</pre>
                                </div>
                              ))}
                            </div>
                          </Collapsible>
                        )}
                      </div>
                    );
                  })()}
                </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {analysisWindow.open && (
        <div className="fixed inset-0 z-60 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setAnalysisWindow({ open: false, text: '', title: '' })}
          />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-3xl mx-4">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="text-lg font-semibold truncate" title={analysisWindow.title}>
                {analysisWindow.title || 'Question Analysis'}
              </h3>
              <button
                className="px-2 py-1 text-sm text-gray-700 hover:text-gray-900"
                onClick={() => setAnalysisWindow({ open: false, text: '', title: '' })}
              >
                Close
              </button>
            </div>
            <div className="p-4 max-h-[70vh] overflow-y-auto">
              <pre className="whitespace-pre-wrap text-sm bg-gray-50 border rounded p-3">
                {analysisWindow.text}
              </pre>
            </div>
          </div>
        </div>
      )}

      {/* Screenshot Lightbox */}
      {showScreenshotLightbox && selectedChallenge?.image_base64 && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          role="dialog"
          aria-modal="true"
          aria-label="Enlarged screenshot"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black bg-opacity-80"
            onClick={() => setShowScreenshotLightbox(false)}
          />
          {/* Modal content */}
          <div className="relative max-w-[95vw] max-h-[90vh] mx-4">
            <button
              aria-label="Close enlarged screenshot"
              className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-black/80 focus:outline-none focus:ring-2 focus:ring-white/70"
              onClick={() => setShowScreenshotLightbox(false)}
            >
              <span className="text-lg leading-none">×</span>
            </button>
            <div className="overflow-auto max-h-[90vh]">
              <img
                src={`data:image/jpeg;base64,${selectedChallenge.image_base64}`}
                alt="challenge full size"
                className="rounded shadow-lg"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CDDIResults;
